import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import {
  defaultPRD2050_R01Dto,
  defaultPRD2050_R01LotDto,
  PRD2050_R01Dto,
  PRD2050_R01LotDto,
} from "./Dto_PRD2050_R01";

export interface PRD2050_R01Filter {
  compCd: string;
  dateRange: DateRange | undefined;
  workplaceCd: string;
  jobTeam: string;
}

/** PRD2050_R01 화면에서 공유하는 조회, 선택, 편집, 그리드 상태와 변경 함수이다. */
export interface Store_PRD2050_R01 {
  PRD2050_R01Filter: PRD2050_R01Filter;
  setPRD2050_R01Filter: (filter: Partial<PRD2050_R01Filter>) => void;

  PRD2050_R01List: PRD2050_R01Dto[];
  setPRD2050_R01List: (list: PRD2050_R01Dto[]) => void;
  selectedPRD2050_R01: PRD2050_R01Dto | undefined;
  setSelectedPRD2050_R01: (data: PRD2050_R01Dto | undefined) => void;

  PRD2050_R01DetailList: PRD2050_R01LotDto[];
  setPRD2050_R01DetailList: (list: PRD2050_R01LotDto[]) => void;
  insertPRD2050_R01DetailList: (list: PRD2050_R01LotDto[]) => void;
  removePRD2050_R01DetailList: (valueIndexList: string[]) => void;
  updatePRD2050_R01DetailRemark: (valueIndex: number, remark: string) => void;

  selectedPRD2050_R01DetailList: string[];
  addSelectedPRD2050_R01DetailList: (valueIndexList: string[]) => void;
  removeSelectedPRD2050_R01DetailList: (valueIndexList: string[]) => void;
  initSelectedPRD2050_R01DetailList: () => void;

  deletedPRD2050_R01DetailList: string[];
  addDeletedPRD2050_R01DetailList: (valueIndexList: string[]) => void;
  removeDeletedPRD2050_R01DetailList: (valueIndexList?: string[]) => void;
  originalPRD2050_R01DetailList: PRD2050_R01LotDto[];

  isModalVisible: boolean;
  setIsModalVisible: (isVisible: boolean) => void;
  PRD2050_R01ModalList: PRD2050_R01LotDto[];
  setPRD2050_R01ModalList: (list: PRD2050_R01LotDto[]) => void;

  columnState: ColumnState[] | null;
  setColumnState: (state: ColumnState[] | null) => void;
  scrollTop: number | null;
  setScrollTop: (top: number | null) => void;
  detailColumnState: ColumnState[] | null;
  setDetailColumnState: (state: ColumnState[] | null) => void;
  detailScrollTop: number | null;
  setDetailScrollTop: (top: number | null) => void;

  initData: () => void;
}

/** 검색조건을 당월 1일부터 오늘까지의 기본 범위로 생성한다. */
const createDefaultPRD2050_R01Filter = (): PRD2050_R01Filter => ({
  compCd: "",
  dateRange: {
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  },
  workplaceCd: "",
  jobTeam: "",
});

/** 같은 품목의 같은 LOT이 상세 목록에 중복 추가되지 않도록 비교 키를 만든다. */
const getPRD2050_R01DetailKey = (row: PRD2050_R01LotDto) =>
  `${row.itemCd}|${row.lotNo}`;

export const usePRD2050_R01Store = create<Store_PRD2050_R01>((set) => ({
  // 검색조건은 부분 변경된 필드만 기존 값에 병합한다.
  PRD2050_R01Filter: createDefaultPRD2050_R01Filter(),
  setPRD2050_R01Filter: (filter) =>
    set((state) => ({
      PRD2050_R01Filter: { ...state.PRD2050_R01Filter, ...filter },
    })),

  // 작업지시 조회 결과에 공통 그리드가 사용하는 순번을 부여한다.
  PRD2050_R01List: [],
  setPRD2050_R01List: (list) =>
    set({
      PRD2050_R01List: list.map((item, index) => ({
        ...defaultPRD2050_R01Dto,
        ...item,
        valueIndex: index + 1,
        orderIndex: index + 1,
        orderIndexSeq: 0,
      })),
    }),
  selectedPRD2050_R01: undefined,
  setSelectedPRD2050_R01: (data) => set({ selectedPRD2050_R01: data }),

  // 서버 상세 조회 결과를 원본 목록과 화면 목록에 함께 보관하고 편집 상태를 초기화한다.
  PRD2050_R01DetailList: [],
  setPRD2050_R01DetailList: (list) => {
    const detailList = list.map((item, index) => ({
      ...defaultPRD2050_R01LotDto,
      ...item,
      valueIndex: index + 1,
      orderIndex: index + 1,
      orderIndexSeq: 0,
      inserted: false,
    }));
    set({
      PRD2050_R01DetailList: detailList,
      originalPRD2050_R01DetailList: detailList,
      deletedPRD2050_R01DetailList: [],
      selectedPRD2050_R01DetailList: [],
    });
  },
  // 모달에서 선택한 LOT을 중복 없이 신규 행(inserted=true)으로 추가한다.
  insertPRD2050_R01DetailList: (list) =>
    set((state) => {
      const existingKeySet = new Set(
        state.PRD2050_R01DetailList.map(getPRD2050_R01DetailKey),
      );
      const detailList = [...state.PRD2050_R01DetailList];
      let nextValueIndex =
        Math.max(0, ...state.PRD2050_R01DetailList.map((item) => item.valueIndex)) + 1;

      list.forEach((item) => {
        const key = getPRD2050_R01DetailKey(item);
        if (existingKeySet.has(key)) return;

        detailList.push({
          ...defaultPRD2050_R01LotDto,
          ...item,
          valueIndex: nextValueIndex,
          orderIndex: nextValueIndex,
          orderIndexSeq: 0,
          lotSeq: 0,
          inserted: true,
        });
        nextValueIndex++;
        existingKeySet.add(key);
      });

      return {
        PRD2050_R01DetailList: detailList,
      };
    }),
  // 저장 전 신규 행 취소 시 지정된 valueIndex 행을 화면 목록에서 제거한다.
  removePRD2050_R01DetailList: (valueIndexList) =>
    set((state) => ({
      PRD2050_R01DetailList: state.PRD2050_R01DetailList
        .filter(
          (item) =>
            !valueIndexList.some(
              (valueIndex) => Number(valueIndex) === item.valueIndex,
            ),
        ),
    })),
  // 셀 편집으로 변경된 비고를 해당 화면 행에 반영한다.
  updatePRD2050_R01DetailRemark: (valueIndex, remark) =>
    set((state) => ({
      PRD2050_R01DetailList: state.PRD2050_R01DetailList.map((item) =>
        item.valueIndex === valueIndex ? { ...item, remark } : item,
      ),
    })),

  // 하단 그리드의 선택 행 valueIndex를 중복 없이 추가·제거·초기화한다.
  selectedPRD2050_R01DetailList: [],
  addSelectedPRD2050_R01DetailList: (valueIndexList) =>
    set((state) => ({
      selectedPRD2050_R01DetailList: Array.from(
        new Set([...state.selectedPRD2050_R01DetailList, ...valueIndexList]),
      ),
    })),
  removeSelectedPRD2050_R01DetailList: (valueIndexList) =>
    set((state) => ({
      selectedPRD2050_R01DetailList:
        state.selectedPRD2050_R01DetailList.filter(
          (valueIndex) => !valueIndexList.includes(valueIndex),
        ),
    })),
  initSelectedPRD2050_R01DetailList: () =>
    set({ selectedPRD2050_R01DetailList: [] }),

  // 서버에 존재하는 행만 삭제 예정 목록에 추가하고, 재클릭 시 삭제 표시를 취소한다.
  deletedPRD2050_R01DetailList: [],
  addDeletedPRD2050_R01DetailList: (valueIndexList) =>
    set((state) => ({
      deletedPRD2050_R01DetailList: Array.from(
        new Set([
          ...state.deletedPRD2050_R01DetailList,
          ...valueIndexList.filter((valueIndex) =>
            state.PRD2050_R01DetailList.some(
              (item) =>
                item.valueIndex === Number(valueIndex) && !item.inserted,
            ),
          ),
        ]),
      ),
    })),
  removeDeletedPRD2050_R01DetailList: (valueIndexList) =>
    set((state) => ({
      deletedPRD2050_R01DetailList: valueIndexList
        ? state.deletedPRD2050_R01DetailList.filter(
            (valueIndex) => !valueIndexList.includes(valueIndex),
          )
        : [],
    })),
  originalPRD2050_R01DetailList: [],

  // LOT 선택 모달의 표시 여부와 조회 결과를 관리한다.
  isModalVisible: false,
  setIsModalVisible: (isVisible) => set({ isModalVisible: isVisible }),
  PRD2050_R01ModalList: [],
  setPRD2050_R01ModalList: (list) =>
    set({
      PRD2050_R01ModalList: list.map((item, index) => ({
        ...defaultPRD2050_R01LotDto,
        ...item,
        valueIndex: index + 1,
        orderIndex: index + 1,
        orderIndexSeq: 0,
        inserted: false,
      })),
    }),

  // 상단·하단 그리드의 사용자 컬럼 설정과 스크롤 위치를 각각 보관한다.
  columnState: null,
  setColumnState: (state) => set({ columnState: state }),
  scrollTop: null,
  setScrollTop: (top) => set({ scrollTop: top }),
  detailColumnState: null,
  setDetailColumnState: (state) => set({ detailColumnState: state }),
  detailScrollTop: null,
  setDetailScrollTop: (top) => set({ detailScrollTop: top }),

  // 화면 초기화 시 검색조건을 포함한 모든 업무·그리드 상태를 기본값으로 되돌린다.
  initData: () =>
    set({
      PRD2050_R01Filter: createDefaultPRD2050_R01Filter(),
      PRD2050_R01List: [],
      selectedPRD2050_R01: undefined,
      PRD2050_R01DetailList: [],
      selectedPRD2050_R01DetailList: [],
      deletedPRD2050_R01DetailList: [],
      originalPRD2050_R01DetailList: [],
      isModalVisible: false,
      PRD2050_R01ModalList: [],
      columnState: null,
      scrollTop: null,
      detailColumnState: null,
      detailScrollTop: null,
    }),
}));
